import type { ChartMatrixData } from '@indutrust/chart-model';
import type { EChartsOption } from 'echarts';
import type { ChartOptionBuildOptions } from './chart-option-chrome';
import {
  buildSharedChrome,
  buildStableGrid,
  buildTooltipStyle,
  hexToRgba,
  pickColor,
} from './chart-option-chrome';
import { resolveEchartsThemeTokens } from './resolve-echarts-theme-tokens';

/**
 * Input for {@link createHeatmapChartOptions}.
 *
 * @example
 * const input: HeatmapChartInput = {
 *   data: { columns: ['Dubai'], rows: ['Metal'], cells: [] },
 *   valueAxisLabel: 'Facilities',
 * };
 */
export type HeatmapChartInput = {
  data: ChartMatrixData;
  /** Legend caption for the color scale, e.g. `'Facilities'`. */
  valueAxisLabel?: string;
  /**
   * Upper bound of the color ramp. Defaults to the largest cell value; pass an
   * explicit max to keep the scale stable while filters narrow the data.
   */
  max?: number;
  /**
   * Caps the row-label gutter in pixels, truncating longer labels.
   *
   * Without it ECharts sizes the gutter to the longest label, and one verbose
   * row name can squeeze the grid into an unreadable strip.
   */
  rowLabelWidth?: number;
  colors?: string[];
};

/**
 * One heatmap cell in ECharts wire format: `[columnIndex, rowIndex, value]`
 * plus two carried-through strings — the short text drawn inside the cell and
 * the full tooltip HTML, which are different lengths and cannot be the same
 * string.
 */
type HeatmapCellTuple = [number, number, number | '-', string, string];

/**
 * Reads one of the carried-through strings out of an ECharts formatter argument.
 *
 * Yields `''` for anything that isn't one of our own tuples, so a formatter
 * renders nothing rather than `undefined` if ECharts ever hands back a shape we
 * didn't produce.
 *
 * @param params - Raw formatter callback argument.
 * @param index - `3` for the in-cell label, `4` for the tooltip HTML.
 */
function readHeatmapCellText(params: unknown, index: 3 | 4): string {
  if (typeof params !== 'object' || params === null || !('value' in params)) {
    return '';
  }
  const { value } = params;
  if (!Array.isArray(value)) {
    return '';
  }
  const text: unknown = value[index];

  return typeof text === 'string' ? text : '';
}

/**
 * Generate an ECharts heatmap for a row × column matrix.
 *
 * Cells with a `null` value are emitted as ECharts' `'-'` empty marker so they
 * stay visible as grid positions without being painted on the color ramp —
 * that is how a privacy-suppressed count reads as "something is here, but the
 * number is withheld" rather than as zero.
 *
 * Unlike the cartesian builders this returns a bare {@link EChartsOption},
 * matching the radar / scatter / gauge builders: its input is a matrix, not
 * `ChartData`, so it cannot be dispatched from `generateChartOptions`.
 *
 * Axis **order** is the caller's business, and callers must account for ECharts
 * drawing category index zero at the *bottom* of a y-axis.
 *
 * @param input Matrix data plus color-scale bounds. {@link HeatmapChartInput}
 * @param buildOptions Optional theme / toolbox overrides.
 * @returns A bare {@link EChartsOption}.
 * @example
 * const opt = createHeatmapChartOptions({
 *   data: {
 *     columns: ['Abu Dhabi', 'Dubai'],
 *     rows: ['Metal Fabrication'],
 *     cells: [{ column: 'Dubai', row: 'Metal Fabrication', value: 58 }],
 *   },
 *   valueAxisLabel: 'Facilities',
 * });
 */
export function createHeatmapChartOptions(
  input: HeatmapChartInput,
  buildOptions: ChartOptionBuildOptions = {}
): EChartsOption {
  const theme = buildOptions.theme ?? resolveEchartsThemeTokens();
  const palette = input.colors ?? theme.palette;
  const { columns, rows, cells } = input.data;
  const columnIndex = new Map(columns.map((label, index) => [label, index]));
  const rowIndex = new Map(rows.map((label, index) => [label, index]));

  const points: HeatmapCellTuple[] = [];
  for (const cell of cells) {
    const x = columnIndex.get(cell.column);
    const y = rowIndex.get(cell.row);
    if (x === undefined || y === undefined) {
      continue;
    }

    const display =
      cell.label ?? (cell.value === null ? '' : String(cell.value));
    points.push([
      x,
      y,
      cell.value ?? '-',
      display,
      cell.tooltip ?? `${cell.row} · ${cell.column}<br/>${display}`,
    ]);
  }

  const observedMax = points.reduce(
    (max, [, , value]) =>
      typeof value === 'number' ? Math.max(max, value) : max,
    0
  );

  // containLabel is forced off by buildStableGrid; reserve the row-label gutter
  // explicitly so truncated Y labels (ROW_LABEL_WIDTH) are not clipped.
  const rowLabelWidth = input.rowLabelWidth ?? 0;
  const gridLeft = rowLabelWidth > 0 ? rowLabelWidth + 8 : 8;

  return {
    ...buildSharedChrome(theme, buildOptions.showToolbox === true),
    tooltip: {
      ...buildTooltipStyle(theme),
      trigger: 'item',
      formatter: (params: unknown): string => readHeatmapCellText(params, 4),
    },
    legend: { show: false },
    grid: buildStableGrid({ left: gridLeft, right: 8, top: 40, bottom: 64 }),
    // Grid lines on both axes rather than alternating split areas. A sparse
    // matrix is mostly empty cells, and without a drawn grid those read as
    // nothing rendered instead of as measured-and-empty — which inverts the
    // meaning of a chart whose blanks are the finding.
    xAxis: {
      type: 'category',
      data: columns,
      splitArea: { show: false },
      splitLine: { show: true, lineStyle: { color: theme.splitLineColor } },
      axisLabel: { color: theme.mutedColor, hideOverlap: true, rotate: 30 },
      axisLine: { lineStyle: { color: theme.borderColor } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'category',
      data: rows,
      splitArea: { show: false },
      splitLine: { show: true, lineStyle: { color: theme.splitLineColor } },
      axisLabel: {
        color: theme.mutedColor,
        overflow: 'truncate',
        width: input.rowLabelWidth,
      },
      axisLine: { lineStyle: { color: theme.borderColor } },
      axisTick: { show: false },
    },
    visualMap: {
      // Pinned to the value slot. Each tuple carries its display label and
      // tooltip after the value, and ECharts otherwise maps the *last*
      // dimension — a string — leaving every cell unpainted while the legend
      // still renders a perfectly good ramp.
      dimension: 2,
      min: 0,
      max: input.max ?? Math.max(observedMax, 1),
      calculable: false,
      orient: 'horizontal',
      left: 'center',
      // Top-aligned so the color scale does not collide with rotated X labels.
      top: 0,
      text: input.valueAxisLabel ? [input.valueAxisLabel, ''] : undefined,
      textStyle: { color: theme.mutedColor },
      inRange: {
        color: [hexToRgba(pickColor(palette, 0), 0.15), pickColor(palette, 0)],
      },
    },
    series: [
      {
        name: input.valueAxisLabel ?? 'Value',
        type: 'heatmap',
        data: points,
        label: {
          show: true,
          color: theme.textColor,
          formatter: (params: unknown): string =>
            readHeatmapCellText(params, 3),
        },
        itemStyle: { borderColor: theme.borderColor, borderWidth: 1 },
        emphasis: {
          itemStyle: { borderColor: theme.textColor, borderWidth: 2 },
        },
      },
    ],
  };
}
